import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { CLIENT_FIELDS, PROJECT_FIELDS, type DocumentClient, type DocumentProject } from '@/features/quotes/queries'
import { describeError, supabase, type Row } from '@/lib/supabase'

/** A contract written in the Studio (29 Oct 2026): a title and its text, marked sent and signed by hand. */
export type Contract = Row<'contracts'> & { contact: DocumentClient | null; job: DocumentProject | null }

const SELECT = `*, contact:contacts!contracts_contact_id_fkey ( ${CLIENT_FIELDS} ), job:jobs!contracts_job_id_fkey ( ${PROJECT_FIELDS} )`
const keys = { all: ['contracts'] as const, one: (id: string) => ['contracts', 'one', id] as const, job: (id: string) => ['contracts', 'job', id] as const }

export function useContracts() {
  return useQuery({
    queryKey: keys.all,
    queryFn: async (): Promise<Contract[]> => {
      const { data, error } = await supabase.from('contracts').select(SELECT).order('created_at', { ascending: false })
      if (error) throw error
      return data as unknown as Contract[]
    },
  })
}

export function useJobContracts(jobId: string) {
  return useQuery({
    queryKey: keys.job(jobId),
    queryFn: async (): Promise<Contract[]> => {
      const { data, error } = await supabase.from('contracts').select(SELECT).eq('job_id', jobId).order('created_at', { ascending: false })
      if (error) throw error
      return data as unknown as Contract[]
    },
  })
}

export function useContract(id: string) {
  return useQuery({
    queryKey: keys.one(id),
    queryFn: async (): Promise<Contract> => {
      const { data, error } = await supabase.from('contracts').select(SELECT).eq('id', id).single()
      if (error) throw error
      return data as unknown as Contract
    },
  })
}

export function useUpdateContract() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<Pick<Row<'contracts'>, 'title' | 'body' | 'status' | 'sent_at' | 'signed_at'>>) => {
      const { error } = await supabase.from('contracts').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not save the contract', { description: describeError(error) }),
  })
}

export function useDeleteContract() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contracts').delete().eq('id', id).eq('status', 'draft')
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not delete the contract', { description: describeError(error) }),
  })
}
